
import { URLS } from '../constants'
import {
    LOGIN_BEGIN, LOGIN_SUCCESS, LOGIN_FAILURE,
    AUTO_LOGIN_BEGIN, AUTO_LOGIN_SUCCESS, AUTO_LOGIN_FAILURE,
    VERIFY_AUTH_COOKIE_BEGIN, VERIFY_AUTH_COOKIE_SUCCESS, VERIFY_AUTH_COOKIE_FAILURE,
    RECONNECT_BEGIN, RECONNECT_SUCCESS, RECONNECT_FAILURE,
    SET_DISCONNECT,
    LOGOUT_BEGIN, LOGOUT_SUCCESS, LOGOUT_FAILURE,
    RECOVERY_PASSWORD_BEGIN, RECOVERY_PASSWORD_SUCCESS, RECOVERY_PASSWORD_FAILURE,
    RESET_PASSWORD_BEGIN, RESET_PASSWORD_SUCCESS, RESET_PASSWORD_FAILURE,
    SET_DARK_MODE
} from '../constants/ActionTypes'

import { dispatchFetchGet, dispatchFetchPost } from './ActionUtil'

// Login
const acPostLoginBegin = () => ({ type: LOGIN_BEGIN })
const acPostLoginSuccess = data => ({ type: LOGIN_SUCCESS, payload: data })
const acPostLoginFailure = err => ({ type: LOGIN_FAILURE, payload: err })

/**
 * Perform a login request with the given email and password.
 *
 * @author Maria Eduarda Franke
 * @param {Object} params - The parameters for the login request.
 * @param {string} params.email - The email of the user.
 * @param {string} params.password - The password of the user.
 * @returns {Promise} A promise that resolves to the result of the login request.
 */
export const acPostLogin = ({ email, password }) => {
    return dispatchFetchPost(
        URLS.LOGIN,
        { email, password },
        acPostLoginBegin,
        acPostLoginSuccess,
        acPostLoginFailure
    )
}

// Auto Login
const acPostAutoLoginBegin = () => ({ type: AUTO_LOGIN_BEGIN })
const acPostAutoLoginSuccess = data => ({ type: AUTO_LOGIN_SUCCESS, payload: data })
const acPostAutoLoginFailure = err => ({ type: AUTO_LOGIN_FAILURE, payload: err })

/**
 * Perform an auto-login request using the provided token.
 *
 * @author Maria Eduarda Franke
 * @param {Object} options - The options for the auto-login request.
 * @param {string} options.token - The token to be used for auto-login.
 * @returns {Promise} A promise that resolves with the result of the auto-login request.
 * @throws {Error} If the auto-login request fails.
 */
export const acPostAutoLogin = ({ token }) => {
    return dispatchFetchPost(
        URLS.AUTO_LOGIN,
        { token },
        acPostAutoLoginBegin,
        acPostAutoLoginSuccess,
        acPostAutoLoginFailure
    )
}

// Verify Auth Cookie
const acVerifyAuthCookieBegin = () => ({ type: VERIFY_AUTH_COOKIE_BEGIN })
const acVerifyAuthCookieSuccess = data => ({ type: VERIFY_AUTH_COOKIE_SUCCESS, payload: data })
const acVerifyAuthCookieFailure = err => ({ type: VERIFY_AUTH_COOKIE_FAILURE, payload: err })

/**
 * Verifies authentication cookie
 *
 * @author Maria Eduarda Franke
 * @returns {Object} The dispatched action object.
 */
export const acVerifyAuthCookie = () => {
    return dispatchFetchGet(
        URLS.VERIFY_AUTH_COOKIE,
        acVerifyAuthCookieBegin,
        acVerifyAuthCookieSuccess,
        acVerifyAuthCookieFailure
    )
}

// Reconnect
const acPostReconnectBegin = () => ({ type: RECONNECT_BEGIN })
const acPostReconnectSuccess = data => ({ type: RECONNECT_SUCCESS, payload: data })
const acPostReconnectFailure = err => ({ type: RECONNECT_FAILURE, payload: err })

/**
 * Dispatches a fetch POST request to the RECONNECT URL with empty body.
 * 
 * @author Maria Eduarda Franke
 * @returns {Promise} A promise that resolves with the response data if the request is successful, 
 *                    or rejects with an error if the request fails.
 * 
 * @throws {Error} If the request fails.
 */
export const acPostReconnect = () => {
    return dispatchFetchPost(
        URLS.RECONNECT,
        {},
        acPostReconnectBegin,
        acPostReconnectSuccess,
        acPostReconnectFailure
    )
}

// Disconnect
export const acSetDisconnect = () => ({ type: SET_DISCONNECT })

// Logout
const acPostLogoutBegin = () => ({ type: LOGOUT_BEGIN })
const acPostLogoutSuccess = data => ({ type: LOGOUT_SUCCESS, payload: data })
const acPostLogoutFailure = err => ({ type: LOGOUT_FAILURE, payload: err })

/**
 * Dispatches a fetch POST request to the LOGOUT URL with empty body.
 * 
 * @author Maria Eduarda Franke
 * @returns {Promise} A promise that resolves with the response data if the request is successful, 
 *                    or rejects with an error if the request fails.
 * 
 * @throws {Error} If the request fails.
 */
export const acLogout = () => {
    return dispatchFetchPost(
        URLS.LOGOUT,
        {},
        acPostLogoutBegin,
        acPostLogoutSuccess,
        acPostLogoutFailure
    )
}

// Password Recovery
const acPostPasswordRecoveryBegin = () => ({ type: RECOVERY_PASSWORD_BEGIN })
const acPostPasswordRecoverySuccess = data => ({ type: RECOVERY_PASSWORD_SUCCESS, payload: data })
const acPostPasswordRecoveryFailure = err => ({ type: RECOVERY_PASSWORD_FAILURE, payload: err })

/**
 * Function to initiate the password recovery process.
 *
 * @author Maria Eduarda Franke
 * @param {Object} params - The parameters for the password recovery.
 * @param {string} params.email - The email address of the user.
 * @returns {Promise} - A promise that resolves when the password recovery request is complete.
 * @throws {Error} - If there is an error during the password recovery process.
 */
export const acPostPasswordRecovery = ({ email }) => {
    return dispatchFetchPost(
        URLS.PASSWORD_RECOVERY,
        { email },
        acPostPasswordRecoveryBegin,
        acPostPasswordRecoverySuccess,
        acPostPasswordRecoveryFailure
    )
}

// Password Reset
const acPostPasswordResetBegin = () => ({ type: RESET_PASSWORD_BEGIN })
const acPostPasswordResetSuccess = data => ({ type: RESET_PASSWORD_SUCCESS, payload: data })
const acPostPasswordResetFailure = err => ({ type: RESET_PASSWORD_FAILURE, payload: err })

/**
 * Function to initiate a password reset request.
 *
 * @author Maria Eduarda Franke
 * @param {Object} params - The parameters for the password reset request.
 * @param {string} params.token - The token for the password reset.
 * @param {string} params.newPassword - The new password to be set.
 * @returns {Promise} - A promise that resolves when the password reset request is complete.
 */
export const acPostPasswordReset = ({ token, newPassword }) => {
    return dispatchFetchPost(
        URLS.PASSWORD_RESET,
        { token, newPassword },
        acPostPasswordResetBegin,
        acPostPasswordResetSuccess,
        acPostPasswordResetFailure
    )
}

// Color Theme
export const acSetDarkMode = data => ({ type: SET_DARK_MODE, payload: data })